/**
 * /pipeline-facility/anomalies/import — read an operator's ILI anomaly list
 * (CSV, pasted rows or XLSX) IN THE BROWSER, show every row for review, then
 * POST /pipeline-facility/anomalies/import/confirm (fieldiq-api#1219).
 *
 * Structured: the sheet needs a header row (lib/pf-anomaly-import). Beside it,
 * PF-3b's AI read (AnomalyAiRead, fieldiq-api#1224) takes a PDF or a free-form
 * sheet and proposes rows for review; both paths end in the same confirm and
 * the same result panel below. While an AI read is under review the CSV path
 * waits, so one review is on screen at a time.
 *
 * THE REVIEW TABLE SHOWS EVERY ROW before anything is sent, with each row's
 * problems beside it; Confirm is offered only when no row has one. The API then
 * dedupes against the site's list. Its answer is said plainly: how many were
 * added, and every skipped row with why — and when NOTHING was added because
 * every row was already there, the screen says that in words, so nobody
 * presses Import again.
 */
import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FileSpreadsheet } from 'lucide-react';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { usePfMeta, usePfSites } from '../../../hooks/usePipelineFacility';
import { useImportPfAnomalies } from '../../../hooks/usePfDigs';
import {
  importResultSentence,
  parseAnomalyText,
  parseAnomalyWorkbook,
  sheetConfirmable,
  SKIP_REASON_LABEL,
  type PfParsedSheet,
} from '../../../lib/pf-anomaly-import';
import { pfDigRefusal } from '../../../lib/pf-digs';
import { blankToNull } from '../../../lib/pipeline-facility';
import type { PfAnomalyImportResult, PfAnomalyImportRow } from '../../../types/pf-digs';
import { useBackToList } from '../../downhole/recordPage';
import { PF_DATE_INPUT, PF_EYEBROW, PF_INPUT, PF_PRIMARY, PF_SECONDARY, PfField, PfFormError, PfPanel } from '../PfBits';
import { PfFormGrid, unitLabel } from './DigBits';
import PfAnomalyAiRead from './AnomalyAiRead';

const COLUMNS: { key: keyof PfAnomalyImportRow; label: string; unit?: 'chainage' | 'wt' }[] = [
  { key: 'feature_id', label: 'Feature ID' },
  { key: 'feature_type', label: 'Type' },
  { key: 'chainage', label: 'Chainage', unit: 'chainage' },
  { key: 'odometer', label: 'Odometer', unit: 'chainage' },
  { key: 'clock_position', label: 'Clock' },
  { key: 'depth_pct_wt', label: 'Depth % WT' },
  { key: 'length', label: 'Length', unit: 'wt' },
  { key: 'width', label: 'Width', unit: 'wt' },
  { key: 'priority', label: 'Priority' },
  { key: 'notes', label: 'Notes' },
];

export default function PfAnomalyImportScreen() {
  const [searchParams] = useSearchParams();
  const back = useBackToList('/pipeline-facility/anomalies');
  const sitesQuery = usePfSites();
  const sites = (sitesQuery.data?.data ?? []).filter((s) => s.status !== 'inactive');
  const units = sitesQuery.data?.meta.units ?? null;
  const importer = useImportPfAnomalies();
  const [siteId, setSiteId] = useState(searchParams.get('siteId') ?? '');
  const [vendor, setVendor] = useState('');
  const [runDate, setRunDate] = useState('');
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [sheet, setSheet] = useState<PfParsedSheet | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [result, setResult] = useState<PfAnomalyImportResult | null>(null);
  // PF-3b: the AI read. `aiLines` is each confirmed row's source reference, for the result's Line column.
  const [aiActive, setAiActive] = useState(false);
  const [aiLines, setAiLines] = useState<string[] | null>(null);
  const [aiKey, setAiKey] = useState(0);
  const featureTypes = usePfMeta().data?.data?.featureTypes ?? [];
  // A header naming a unit is checked against the company's units, so nothing
  // is read until they are known — reading early refused every unit column.
  const unitsKnown = !!units?.chainage && !!units?.wallThickness;
  const importUnits = useMemo(() => ({ chainage: units?.chainage ?? null, wallThickness: units?.wallThickness ?? null }), [units]);

  const reset = () => {
    setSheet(null);
    setResult(null);
    setAiLines(null);
    setSendError(null);
    setReadError(null);
  };

  const readText = () => {
    reset();
    if (!text.trim()) return setReadError('Paste the anomaly list, header row first, or choose a file.');
    setFileName(null);
    setSheet(parseAnomalyText(text, importUnits));
  };

  const readFile = async (file: File | undefined) => {
    reset();
    if (!file) return;
    setFileName(file.name);
    try {
      if (/\.xlsx?$/i.test(file.name)) setSheet(parseAnomalyWorkbook(await file.arrayBuffer(), importUnits));
      else setSheet(parseAnomalyText(await file.text(), importUnits));
    } catch {
      setReadError(`${file.name} could not be read. Save it as CSV or XLSX and try again.`);
    }
  };

  const errorCount = sheet ? sheet.rows.filter((r) => r.errors.length).length : 0;
  const confirmable = sheetConfirmable(sheet) && !!siteId && !result;

  const confirm = () => {
    if (!sheet || !siteId) return;
    setSendError(null);
    importer.mutate(
      {
        site_id: siteId,
        ili_vendor: blankToNull(vendor),
        ili_run_date: blankToNull(runDate),
        rows: sheet.rows.map((r) => r.row as PfAnomalyImportRow),
      },
      {
        onSuccess: (r) => setResult(r),
        onError: (err) => setSendError(pfDigRefusal(err, 'The list could not be imported. Nothing was saved.')),
      },
    );
  };

  const nothingAdded = !!result && result.counts.inserted === 0 && result.counts.skipped > 0;

  return (
    <div data-testid="pf-anomaly-import-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={PF_EYEBROW}
        title="Import anomaly list"
        description="Read the operator's ILI list, check every row, then add it to the site."
        onBack={back}
        backLabel="Back"
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <PfPanel eyebrow="Step 1" title="Site and source">
            <div className="space-y-4 px-4 py-4">
              <PfFormGrid>
                <PfField label="Site">
                  <select className={PF_INPUT} aria-label="Site" value={siteId} onChange={(e) => { setSiteId(e.target.value); setResult(null); }}>
                    <option value="">Choose a site</option>
                    {sites.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </PfField>
                <PfField label="ILI vendor" hint="Used for rows that do not name one">
                  <input className={PF_INPUT} aria-label="ILI vendor" value={vendor} onChange={(e) => setVendor(e.target.value)} />
                </PfField>
                <PfField label="ILI run date" hint="Used for rows that do not name one">
                  <input type="date" className={PF_DATE_INPUT} aria-label="ILI run date" value={runDate} onChange={(e) => setRunDate(e.target.value)} />
                </PfField>
              </PfFormGrid>
              <p className="text-xs text-fieldiq-muted">
                Columns: Feature ID, Feature type (metal loss, dent, crack-like, girth weld, other), {unitLabel('Chainage', units?.chainage)},{' '}
                {unitLabel('Odometer', units?.chainage)}, Clock, Depth (%), {unitLabel('Length', units?.wallThickness)}, {unitLabel('Width', units?.wallThickness)},
                Priority, Notes. Lengths must already be in this company&apos;s units — a column named in other units is refused, not converted.
              </p>
              <div className="grid gap-3 lg:grid-cols-2">
                <PfField label="Paste rows (header first)">
                  <textarea
                    className={`${PF_INPUT} h-40 py-2 font-mono text-xs`}
                    aria-label="Paste anomaly rows"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={'Feature ID,Feature type,Chainage,Clock,Depth (%),Length,Width\nML-0142,metal loss,1250.4,4:30,38,42,25'}
                  />
                </PfField>
                <div className="space-y-3">
                  <PfField label="Or choose a CSV / XLSX file">
                    <input
                      type="file"
                      accept=".csv,.txt,.xlsx,.xls,text/csv"
                      aria-label="Anomaly list file"
                      className="block w-full text-sm text-fieldiq-text file:mr-3 file:rounded-fi-control file:border file:border-fieldiq-border file:bg-fieldiq-card file:px-3 file:py-2 file:text-sm file:text-white"
                      disabled={!unitsKnown || aiActive}
                      onChange={(e) => readFile(e.target.files?.[0])}
                    />
                  </PfField>
                  {fileName && (
                    <p className="flex items-center gap-2 text-sm text-fieldiq-text">
                      <FileSpreadsheet className="size-4" aria-hidden="true" />
                      {fileName}
                    </p>
                  )}
                  <button type="button" className={PF_SECONDARY} onClick={readText} disabled={!unitsKnown || aiActive}>
                    Read pasted rows
                  </button>
                  {aiActive && <p className="text-sm text-fieldiq-muted">Save or discard the AI&apos;s rows below first.</p>}
                  {!unitsKnown && (
                    <p className="text-sm text-fieldiq-muted" data-testid="pf-import-units-pending">
                      {sitesQuery.isLoading ? "Loading this company's units…" : "This company's units could not be read, so a list cannot be checked. Reload the page."}
                    </p>
                  )}
                </div>
              </div>
              <PfFormError message={readError} />
            </div>
          </PfPanel>

          {!result && (
            <PfAnomalyAiRead
              key={aiKey}
              siteId={siteId}
              vendor={vendor}
              runDate={runDate}
              units={units}
              featureTypes={featureTypes}
              onActiveChange={setAiActive}
              onStart={() => { reset(); setText(''); setFileName(null); }}
              onResult={(r, lines) => { setResult(r); setAiLines(lines); }}
            />
          )}

          {sheet && (
            <PfPanel
              eyebrow="Step 2"
              title="Review every row"
              testId="pf-import-review"
              footer={`${sheet.rows.length} row${sheet.rows.length === 1 ? '' : 's'} read${errorCount ? ` · ${errorCount} with problems` : ''}`}
            >
              <div className="space-y-3 px-4 py-4">
                {sheet.sheetErrors.map((e) => (
                  <PfFormError key={e} message={e} />
                ))}
                {errorCount > 0 && (
                  <PfFormError message={`${errorCount} row${errorCount === 1 ? ' has' : 's have'} a problem. Fix the list and read it again — nothing is sent while any row has one.`} />
                )}
                {sheet.ignored.length > 0 && (
                  <p className="text-xs text-fieldiq-muted">Columns not read: {sheet.ignored.join(', ')}.</p>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[980px] text-left text-sm" data-testid="pf-import-review-table">
                    <thead>
                      <tr className="border-b border-fieldiq-border text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                        <th className="px-2 py-2">Line</th>
                        {COLUMNS.map((c) => (
                          <th key={c.key} className="px-2 py-2">
                            {c.unit ? unitLabel(c.label, c.unit === 'chainage' ? units?.chainage : units?.wallThickness) : c.label}
                          </th>
                        ))}
                        <th className="px-2 py-2">Check</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sheet.rows.map((r) => (
                        <tr
                          key={r.line}
                          data-testid="pf-import-row"
                          className={`border-b border-fieldiq-border/60 border-l-4 ${r.errors.length ? 'border-l-fi-red' : 'border-l-transparent'}`}
                        >
                          <td className="px-2 py-2 font-mono text-fieldiq-muted">{r.line}</td>
                          {COLUMNS.map((c) => (
                            <td key={c.key} className="px-2 py-2 font-mono text-fieldiq-text">
                              {r.row && c.key === 'feature_type' ? String(r.row.feature_type).replace(/_/g, ' ') : r.cells[c.key] || '—'}
                            </td>
                          ))}
                          <td className="px-2 py-2 text-xs">
                            {r.errors.length ? (
                              <ul className="space-y-1 text-fieldiq-text" role="alert">
                                {r.errors.map((e) => (
                                  <li key={e}>{e}</li>
                                ))}
                              </ul>
                            ) : (
                              <span className="text-fieldiq-muted">Ready</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <PfFormError message={sendError} />
                {!siteId && <p className="text-sm text-fieldiq-text">Choose the site before importing.</p>}
                {!result && (
                  <div className="flex justify-end">
                    <button type="button" className={PF_PRIMARY} disabled={!confirmable || importer.isPending} onClick={confirm} data-testid="pf-import-confirm">
                      {importer.isPending ? 'Importing…' : `Import ${sheet.rows.length} row${sheet.rows.length === 1 ? '' : 's'}`}
                    </button>
                  </div>
                )}
              </div>
            </PfPanel>
          )}

          {result && (
            <PfPanel eyebrow="Step 3" title="Result" testId="pf-import-result">
              <div className="space-y-3 px-4 py-4">
                <p
                  data-testid="pf-import-result-sentence"
                  data-outcome={nothingAdded ? 'nothing-added' : 'added'}
                  className={`rounded-fi-control border px-4 py-3 text-base font-bold text-fieldiq-text ${
                    nothingAdded ? 'border-fi-amber bg-fi-signal-amber/10 border-l-4 border-l-fi-amber' : 'border-fi-green/50 bg-fi-signal-green/10'
                  }`}
                >
                  {importResultSentence(result.counts)}
                </p>
                {result.skipped.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[720px] text-left text-sm" data-testid="pf-import-skipped">
                      <thead>
                        <tr className="border-b border-fieldiq-border text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                          <th className="px-2 py-2">Line</th>
                          <th className="px-2 py-2">Feature ID</th>
                          <th className="px-2 py-2">Skipped because</th>
                          <th className="px-2 py-2">Detail</th>
                          <th className="px-2 py-2" />
                        </tr>
                      </thead>
                      <tbody>
                        {result.skipped.map((s) => (
                          <tr key={`${s.index}-${s.reason}`} data-testid="pf-import-skip" className="border-b border-fieldiq-border/60">
                            <td className="px-2 py-2 font-mono text-fieldiq-muted">
                              {aiLines ? aiLines[s.index] ?? '—' : sheet ? sheet.rows[s.index]?.line ?? '—' : '—'}
                            </td>
                            <td className="px-2 py-2 font-mono text-white">{s.featureId || '—'}</td>
                            <td className="px-2 py-2 text-fieldiq-text">{SKIP_REASON_LABEL[s.reason] ?? s.reason}</td>
                            <td className="px-2 py-2 text-xs text-fieldiq-text">{s.message || '—'}</td>
                            <td className="px-2 py-2">
                              {s.existingId && (
                                <Link className="text-sm font-bold text-fi-amber underline" to={`/pipeline-facility/anomalies/${s.existingId}`}>
                                  Open existing
                                </Link>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="flex flex-wrap justify-end gap-2">
                  <button type="button" className={PF_SECONDARY} onClick={() => { reset(); setText(''); setFileName(null); setAiKey((k) => k + 1); }}>
                    Import another list
                  </button>
                  <Link className={PF_PRIMARY} to={`/pipeline-facility/anomalies?siteId=${siteId}`}>
                    View the site&apos;s anomalies
                  </Link>
                </div>
              </div>
            </PfPanel>
          )}
        </div>
      </section>
    </div>
  );
}
